import {
    BcmOrderStatusType,
    BcmOrderTaskPositionStatusType,
} from '../models/bcm-order-status';

export {
    BcmOrderStatusType,
    BcmOrderTaskPositionStatusType,
};

export const ACTIVE_ORDER_STATUS_TYPES: BcmOrderStatusType[] = [
    BcmOrderStatusType.Open,
    BcmOrderStatusType.Processing,
    BcmOrderStatusType.Paused,
    BcmOrderStatusType.WaitingForMaterial,
    BcmOrderStatusType.WaitingForCustomer,
    BcmOrderStatusType.WaitingForExternal,
    BcmOrderStatusType.ReadyForBilling,
    BcmOrderStatusType.Done,
];

export const LEGACY_ORDER_STATUS_TYPES: BcmOrderStatusType[] = [
    BcmOrderStatusType.Cancelled,
    BcmOrderStatusType.Payed,
];

export const ALL_ORDER_STATUS_TYPES: BcmOrderStatusType[] = [
    ...ACTIVE_ORDER_STATUS_TYPES,
    ...LEGACY_ORDER_STATUS_TYPES,
];

export const ALL_TASK_POSITION_STATUS_TYPES: BcmOrderTaskPositionStatusType[] = [
    BcmOrderTaskPositionStatusType.Open,
    BcmOrderTaskPositionStatusType.InProgress,
    BcmOrderTaskPositionStatusType.WaitingExternal,
    BcmOrderTaskPositionStatusType.WaitingMaterial,
    BcmOrderTaskPositionStatusType.WaitingForCustomer,
    BcmOrderTaskPositionStatusType.Paused,
    BcmOrderTaskPositionStatusType.Done,
];

const ORDER_STATUS_TRANSITIONS: Record<BcmOrderStatusType, BcmOrderStatusType[]> = {
    [BcmOrderStatusType.None]: [BcmOrderStatusType.Open],
    [BcmOrderStatusType.Open]: [
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.Paused,
        BcmOrderStatusType.WaitingForMaterial,
        BcmOrderStatusType.WaitingForCustomer,
        BcmOrderStatusType.WaitingForExternal,
        BcmOrderStatusType.Cancelled,
    ],
    [BcmOrderStatusType.Processing]: [
        BcmOrderStatusType.Open,
        BcmOrderStatusType.Paused,
        BcmOrderStatusType.WaitingForMaterial,
        BcmOrderStatusType.WaitingForCustomer,
        BcmOrderStatusType.WaitingForExternal,
        BcmOrderStatusType.ReadyForBilling,
        BcmOrderStatusType.Done,
        BcmOrderStatusType.Cancelled,
    ],
    [BcmOrderStatusType.Paused]: [
        BcmOrderStatusType.Open,
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.WaitingForMaterial,
        BcmOrderStatusType.WaitingForCustomer,
        BcmOrderStatusType.WaitingForExternal,
    ],
    [BcmOrderStatusType.WaitingForMaterial]: [
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.Paused,
        BcmOrderStatusType.ReadyForBilling,
    ],
    [BcmOrderStatusType.WaitingForCustomer]: [
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.Paused,
        BcmOrderStatusType.ReadyForBilling,
    ],
    [BcmOrderStatusType.WaitingForExternal]: [
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.Paused,
        BcmOrderStatusType.ReadyForBilling,
    ],
    [BcmOrderStatusType.ReadyForBilling]: [
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.Done,
        BcmOrderStatusType.Payed,
    ],
    [BcmOrderStatusType.Done]: [
        BcmOrderStatusType.Processing,
        BcmOrderStatusType.ReadyForBilling,
        BcmOrderStatusType.Payed,
    ],
    [BcmOrderStatusType.Payed]: [
        BcmOrderStatusType.ReadyForBilling,
    ],
    [BcmOrderStatusType.Cancelled]: [
        BcmOrderStatusType.Open,
    ],
};

const TASK_POSITION_STATUS_TRANSITIONS: Record<BcmOrderTaskPositionStatusType, BcmOrderTaskPositionStatusType[]> = {
    [BcmOrderTaskPositionStatusType.Open]: [
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.Paused,
        BcmOrderTaskPositionStatusType.WaitingExternal,
        BcmOrderTaskPositionStatusType.WaitingMaterial,
        BcmOrderTaskPositionStatusType.WaitingForCustomer,
        BcmOrderTaskPositionStatusType.Done,
    ],
    [BcmOrderTaskPositionStatusType.InProgress]: [
        BcmOrderTaskPositionStatusType.Open,
        BcmOrderTaskPositionStatusType.Paused,
        BcmOrderTaskPositionStatusType.WaitingExternal,
        BcmOrderTaskPositionStatusType.WaitingMaterial,
        BcmOrderTaskPositionStatusType.WaitingForCustomer,
        BcmOrderTaskPositionStatusType.Done,
    ],
    [BcmOrderTaskPositionStatusType.Paused]: [
        BcmOrderTaskPositionStatusType.Open,
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.WaitingExternal,
        BcmOrderTaskPositionStatusType.WaitingMaterial,
        BcmOrderTaskPositionStatusType.WaitingForCustomer,
        BcmOrderTaskPositionStatusType.Done,
    ],
    [BcmOrderTaskPositionStatusType.WaitingExternal]: [
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.Paused,
        BcmOrderTaskPositionStatusType.Done,
    ],
    [BcmOrderTaskPositionStatusType.WaitingMaterial]: [
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.Paused,
        BcmOrderTaskPositionStatusType.Done,
    ],
    [BcmOrderTaskPositionStatusType.WaitingForCustomer]: [
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.Paused,
        BcmOrderTaskPositionStatusType.Done,
    ],
    [BcmOrderTaskPositionStatusType.Done]: [
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.Open,
    ],
};

export const ORDER_STATUS_NOTE_MAX_LENGTH = 1024;

export function normalizeTaskPositionStatusNote(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();

    return trimmed ? trimmed : null;
}

export function normalizeOrderStatusNote(value: unknown): string | null {
    return normalizeTaskPositionStatusNote(value);
}

const TASK_POSITION_STATUS_NOTE_REQUIRED: BcmOrderTaskPositionStatusType[] = [
    BcmOrderTaskPositionStatusType.Paused,
    BcmOrderTaskPositionStatusType.WaitingExternal,
    BcmOrderTaskPositionStatusType.WaitingMaterial,
    BcmOrderTaskPositionStatusType.WaitingForCustomer,
];

export function isTaskPositionStatusNoteRequired(currentStatus: unknown, nextStatus: unknown): boolean {
    const current = normalizeTaskPositionStatus(currentStatus);
    const next = normalizeTaskPositionStatus(nextStatus);

    if (current === next) {
        return false;
    }

    return TASK_POSITION_STATUS_NOTE_REQUIRED.includes(next);
}

export function validateTaskPositionStatusChangeNote(
    currentStatus: unknown,
    nextStatus: unknown,
    note: unknown,
): { ok: boolean; errorKey?: string } {
    if (!isTaskPositionStatusNoteRequired(currentStatus, nextStatus)) {
        return { ok: true };
    }

    if (!normalizeTaskPositionStatusNote(note)) {
        return { ok: false, errorKey: 'bcmErrors.orderTaskPosition.reasonRequired' };
    }

    return { ok: true };
}

export function isOrderStatusNoteRequired(currentStatus: unknown, nextStatus: unknown): boolean {
    const current = normalizeOrderStatus(currentStatus);
    const next = normalizeOrderStatus(nextStatus);

    if (current === next) {
        return false;
    }

    if (next === BcmOrderStatusType.Cancelled) {
        return true;
    }

    if (ORDER_STATUS_OVERRIDE_FOR_TASK_DERIVATION.includes(next)) {
        return true;
    }

    if (next === BcmOrderStatusType.Processing) {
        return current === BcmOrderStatusType.Done
            || current === BcmOrderStatusType.Payed
            || current === BcmOrderStatusType.ReadyForBilling;
    }

    return false;
}

export function validateOrderStatusChangeNote(
    currentStatus: unknown,
    nextStatus: unknown,
    note: unknown,
): { ok: boolean; errorKey?: string } {
    if (!isOrderStatusNoteRequired(currentStatus, nextStatus)) {
        return { ok: true };
    }

    if (!normalizeOrderStatusNote(note)) {
        return { ok: false, errorKey: 'bcmErrors.orderStatus.reasonRequired' };
    }

    return { ok: true };
}

export function validateReadyForBillingTransition(
    taskPositions: OrderTaskPositionStatusSnapshot[] | null | undefined,
): { ok: boolean; errorKey?: string; pendingTaskCount?: number; totalTaskCount?: number } {
    const snapshots = taskPositions || [];
    const totalTaskCount = snapshots.length;

    if (totalTaskCount === 0) {
        return {
            ok: false,
            errorKey: 'bcmErrors.orderStatus.noTasksForBilling',
            pendingTaskCount: 0,
            totalTaskCount: 0,
        };
    }

    const pendingTaskCount = snapshots.filter((taskPosition) => {
        const status = normalizeTaskPositionStatus(taskPosition.taskStatus, taskPosition.done);

        return status !== BcmOrderTaskPositionStatusType.Done;
    }).length;

    if (pendingTaskCount > 0) {
        return {
            ok: false,
            errorKey: 'bcmErrors.orderStatus.notReadyForBilling',
            pendingTaskCount,
            totalTaskCount,
        };
    }

    return { ok: true, pendingTaskCount: 0, totalTaskCount };
}

export interface OrderStatusChangeInput {
    currentStatus: unknown;
    nextStatus: unknown;
    note?: unknown;
    taskPositions?: OrderTaskPositionStatusSnapshot[] | null;
}

export function validateOrderStatusChange(
    input: OrderStatusChangeInput,
): { ok: boolean; errorKey?: string; pendingTaskCount?: number; totalTaskCount?: number } {
    const transitionCheck = validateOrderStatusTransition(input.currentStatus, input.nextStatus);

    if (!transitionCheck.ok) {
        return transitionCheck;
    }

    const noteCheck = validateOrderStatusChangeNote(
        input.currentStatus,
        input.nextStatus,
        input.note,
    );

    if (!noteCheck.ok) {
        return noteCheck;
    }

    const next = normalizeOrderStatus(input.nextStatus);

    if (next === BcmOrderStatusType.ReadyForBilling) {
        return validateReadyForBillingTransition(input.taskPositions);
    }

    return { ok: true };
}

export function filterSelectableOrderStatusTransitions(
    currentStatus: unknown,
    taskPositions: OrderTaskPositionStatusSnapshot[] | null | undefined,
): BcmOrderStatusType[] {
    return getAllowedOrderStatusTransitions(currentStatus).filter((status) => {
        if (status !== BcmOrderStatusType.ReadyForBilling) {
            return true;
        }

        return validateReadyForBillingTransition(taskPositions).ok;
    });
}

export function isKnownOrderStatus(value: unknown): value is BcmOrderStatusType {
    return typeof value === 'string' && ALL_ORDER_STATUS_TYPES.includes(value as BcmOrderStatusType);
}

export function isKnownTaskPositionStatus(value: unknown): value is BcmOrderTaskPositionStatusType {
    return typeof value === 'string' && ALL_TASK_POSITION_STATUS_TYPES.includes(value as BcmOrderTaskPositionStatusType);
}

export function normalizeOrderStatus(value: unknown): BcmOrderStatusType {
    if (isKnownOrderStatus(value)) {
        return value;
    }

    return BcmOrderStatusType.Open;
}

export function normalizeTaskPositionStatus(value: unknown, done?: boolean | null): BcmOrderTaskPositionStatusType {
    if (isKnownTaskPositionStatus(value)) {
        return value;
    }

    if (done === true) {
        return BcmOrderTaskPositionStatusType.Done;
    }

    return BcmOrderTaskPositionStatusType.Open;
}

export function isOrderStatusLockedForEditing(status: BcmOrderStatusType): boolean {
    return status === BcmOrderStatusType.Done
        || status === BcmOrderStatusType.Payed
        || status === BcmOrderStatusType.Cancelled;
}

export function validateOrderStatusTransition(
    currentStatus: unknown,
    nextStatus: unknown,
): { ok: boolean; errorKey?: string } {
    const current = normalizeOrderStatus(currentStatus);
    const next = normalizeOrderStatus(nextStatus);

    if (!isKnownOrderStatus(nextStatus)) {
        return { ok: false, errorKey: 'bcmErrors.orderStatus.invalidStatus' };
    }

    if (current === next) {
        return { ok: true };
    }

    const allowedTargets = ORDER_STATUS_TRANSITIONS[current] || [];

    if (!allowedTargets.includes(next)) {
        return { ok: false, errorKey: 'bcmErrors.orderStatus.invalidTransition' };
    }

    return { ok: true };
}

export function validateTaskPositionStatusTransition(
    currentStatus: unknown,
    nextStatus: unknown,
): { ok: boolean; errorKey?: string } {
    const current = normalizeTaskPositionStatus(currentStatus);
    const next = normalizeTaskPositionStatus(nextStatus);

    if (!isKnownTaskPositionStatus(nextStatus)) {
        return { ok: false, errorKey: 'bcmErrors.orderTaskPosition.invalidStatus' };
    }

    if (current === next) {
        return { ok: true };
    }

    const allowedTargets = TASK_POSITION_STATUS_TRANSITIONS[current] || [];

    if (!allowedTargets.includes(next)) {
        return { ok: false, errorKey: 'bcmErrors.orderTaskPosition.invalidTransition' };
    }

    return { ok: true };
}

export function resolveTaskPositionDoneFlag(status: BcmOrderTaskPositionStatusType): boolean {
    return status === BcmOrderTaskPositionStatusType.Done;
}

export function getAllowedOrderStatusTransitions(currentStatus: unknown): BcmOrderStatusType[] {
    const current = normalizeOrderStatus(currentStatus);

    return ORDER_STATUS_TRANSITIONS[current] || [];
}

export function getAllowedTaskPositionStatusTransitions(
    currentStatus: unknown,
): BcmOrderTaskPositionStatusType[] {
    const current = normalizeTaskPositionStatus(currentStatus);

    return TASK_POSITION_STATUS_TRANSITIONS[current] || [];
}

export const ORDER_STATUS_OVERRIDE_FOR_TASK_DERIVATION: BcmOrderStatusType[] = [
    BcmOrderStatusType.Paused,
    BcmOrderStatusType.WaitingForMaterial,
    BcmOrderStatusType.WaitingForCustomer,
    BcmOrderStatusType.WaitingForExternal,
];

export interface OrderTaskPositionStatusSnapshot {
    taskStatus?: unknown;
    done?: boolean | null;
}

/** Aufgabe wurde bearbeitet (Zeit, Pause, Warten) – Auftrag bleibt „in Arbeit“, solange nicht alles erledigt ist. */
export function isTaskPositionStatusIndicatingOrderWork(
    status: BcmOrderTaskPositionStatusType,
): boolean {
    return [
        BcmOrderTaskPositionStatusType.InProgress,
        BcmOrderTaskPositionStatusType.Paused,
        BcmOrderTaskPositionStatusType.WaitingExternal,
        BcmOrderTaskPositionStatusType.WaitingMaterial,
        BcmOrderTaskPositionStatusType.WaitingForCustomer,
    ].includes(status);
}

export function isOrderStatusOverrideForTaskDerivation(status: unknown): boolean {
    const normalized = normalizeOrderStatus(status);

    return ORDER_STATUS_OVERRIDE_FOR_TASK_DERIVATION.includes(normalized);
}

export function canDeriveOrderStatusFromTaskPositions(status: unknown): boolean {
    const normalized = normalizeOrderStatus(status);

    if (isOrderStatusLockedForEditing(normalized)) {
        return false;
    }

    if (isOrderStatusOverrideForTaskDerivation(normalized)) {
        return false;
    }

    return normalized === BcmOrderStatusType.Open
        || normalized === BcmOrderStatusType.Processing
        || normalized === BcmOrderStatusType.ReadyForBilling;
}

export function resolveDerivedOrderStatusFromTaskPositions(
    taskPositions: OrderTaskPositionStatusSnapshot[] | null | undefined,
    currentOrderStatus: unknown,
): BcmOrderStatusType | null {
    if (!canDeriveOrderStatusFromTaskPositions(currentOrderStatus)) {
        return null;
    }

    const statuses = (taskPositions || [])
        .map((taskPosition) => normalizeTaskPositionStatus(taskPosition.taskStatus, taskPosition.done));

    if (!statuses.length) {
        return null;
    }

    const allOpen = statuses.every((status) => status === BcmOrderTaskPositionStatusType.Open);
    const allDone = statuses.every((status) => status === BcmOrderTaskPositionStatusType.Done);
    const hasStartedWork = statuses.some(
        (status) => isTaskPositionStatusIndicatingOrderWork(status) || status === BcmOrderTaskPositionStatusType.Done,
    );

    if (allOpen) {
        return BcmOrderStatusType.Open;
    }

    if (allDone) {
        return BcmOrderStatusType.ReadyForBilling;
    }

    if (hasStartedWork) {
        return BcmOrderStatusType.Processing;
    }

    return BcmOrderStatusType.Open;
}

export function resolveOrderStatusAfterTaskPositionChange(
    taskPositions: OrderTaskPositionStatusSnapshot[] | null | undefined,
    currentOrderStatus: unknown,
): BcmOrderStatusType | null {
    const derivedStatus = resolveDerivedOrderStatusFromTaskPositions(taskPositions, currentOrderStatus);
    const current = normalizeOrderStatus(currentOrderStatus);

    if (!derivedStatus || derivedStatus === current) {
        return null;
    }

    const transitionCheck = validateOrderStatusTransition(current, derivedStatus);

    if (!transitionCheck.ok) {
        return null;
    }

    return derivedStatus;
}

export function resolveOrderStatusAfterOpenTaskEntersProgress(
    taskPositions: OrderTaskPositionStatusSnapshot[] | null | undefined,
    currentOrderStatus: unknown,
): BcmOrderStatusType | null {
    const current = normalizeOrderStatus(currentOrderStatus);

    if (current !== BcmOrderStatusType.Open) {
        return resolveOrderStatusAfterTaskPositionChange(taskPositions, currentOrderStatus);
    }

    const hasInProgress = (taskPositions || []).some(
        (taskPosition) => normalizeTaskPositionStatus(taskPosition.taskStatus, taskPosition.done)
            === BcmOrderTaskPositionStatusType.InProgress,
    );

    if (!hasInProgress) {
        return null;
    }

    const transitionCheck = validateOrderStatusTransition(current, BcmOrderStatusType.Processing);

    return transitionCheck.ok ? BcmOrderStatusType.Processing : null;
}

export function resolveOrderStatusAfterAllTasksCompleted(
    taskPositions: OrderTaskPositionStatusSnapshot[] | null | undefined,
    currentOrderStatus: unknown,
): BcmOrderStatusType | null {
    if (!canDeriveOrderStatusFromTaskPositions(currentOrderStatus)) {
        return null;
    }

    const statuses = (taskPositions || [])
        .map((taskPosition) => normalizeTaskPositionStatus(taskPosition.taskStatus, taskPosition.done));

    if (!statuses.length) {
        return null;
    }

    const allDone = statuses.every((status) => status === BcmOrderTaskPositionStatusType.Done);

    if (!allDone) {
        return null;
    }

    const current = normalizeOrderStatus(currentOrderStatus);

    if (current === BcmOrderStatusType.ReadyForBilling) {
        return null;
    }

    if (current === BcmOrderStatusType.Open || current === BcmOrderStatusType.Processing) {
        return BcmOrderStatusType.ReadyForBilling;
    }

    return resolveOrderStatusAfterTaskPositionChange(taskPositions, currentOrderStatus);
}

export function resolveTaskPositionStatusAfterTimeEntry(
    currentStatus: unknown,
    done?: boolean | null,
): BcmOrderTaskPositionStatusType | null {
    const current = normalizeTaskPositionStatus(currentStatus, done);

    if (current !== BcmOrderTaskPositionStatusType.Open) {
        return null;
    }

    const nextStatus = BcmOrderTaskPositionStatusType.InProgress;
    const transitionCheck = validateTaskPositionStatusTransition(current, nextStatus);

    return transitionCheck.ok ? nextStatus : null;
}
